import { GraphQL } from "@rpi/openapi-api";
import { Flex, RpiButton, RpiText } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { AppPage } from "../../components/AppPage";
import { Pagination } from "../../components/Pagination";
import { RpiTableContainer } from "../../components/RpiTableContainer";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { SearchFilterBar } from "../../components/search-filter/SearchFilterBar";
import { SearchInput, toSearchInputArray } from "../../configuration/filters/searchInput";
import { apiCredentialColumns } from "../../configuration/columns/apiCredential.column";
import { useApiCredentialFilters } from "../../configuration/filters/apiCredential.filter";
import { featureFlags } from "../../featureFlags";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../hooks/usePagination";
import { RpiTable } from "../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../utils/graphql.util";
import { apiCredentialSearchLabels } from "../../utils/searchField.util";
import { ApiCredentialsFilter } from "./ApiCredentialsFilter";

export const ApiCredentialsView: React.FC = () => {
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.ApiCredentialsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.ApiCredentialSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.ApiCredentialsQueryVariables>>();

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (
      filters: SearchInput<GraphQL.ApiCredentialSearchFields>[]
    ) => SearchInput<GraphQL.ApiCredentialSearchFields>[]
  ) => {
    setSearchInputs((prev) => recipe(toSearchInputArray(prev)));
  };

  const sandboxFilter: GraphQL.ApiCredentialSearchInput = {
    field: GraphQL.ApiCredentialSearchFields.IsSandbox,
    condition: GraphQL.SearchCondition.Equals,
    firstValue: user.isSandbox.toString(),
  };
  const searchInputsWithSandboxFilter = [...(searchInputs ?? []), sandboxFilter];

  const pagination = useRpiPagination({ perPage: 20 });

  const apiCredentialFilters = useApiCredentialFilters();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error, refetch } = GraphQL.useApiCredentialsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputsWithSandboxFilter,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.apiCredentials.totalCount),
    }
  );
  const apiCredentials = React.useMemo(() => data?.apiCredentials.data, [data]);

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  return (
    <AppPage path={["API Credentials"]} onReload={refetch}>
      {outlet}

      {!user.is.SUPER_ADMIN && (
        <Flex.Row align="center" gap={8} mb={16}>
          <RpiText
            type="h3"
            weight="regular"
            color={(theme) => theme.colors.text[5]}
            sx={{ flexShrink: 0 }}
            children="Webhook Secret:"
          />
          <Flex.Row
            gap={8}
            px={8}
            sx={(theme) => ({
              background: theme.colors.sky[7],
              borderRadius: 4,
            })}
          >
            <RpiText
              type="p3"
              weight="regular"
              sx={{ fontFamily: "monospace" }}
              children={apiCredentials?.[0]?.webhookSecret}
            />
          </Flex.Row>
        </Flex.Row>
      )}

      <AppPage.Section>
        {featureFlags.newSearchExperience ? (
          user.is.SUPER_ADMIN && (
            <SearchFilterBar
              placeholder="Search by API Key"
              isLoading={isLoading}
              filters={searchFilters}
              onSetFilters={setSearchFilters}
              onSearchSubmit={handleSearchSubmit}
              onFilterClick={() => setIsFilterOpen(true)}
              filterLabels={apiCredentialSearchLabels}
            />
          )
        ) : (
          <RpiSearchFilter
            placeholder="Search by Api Key"
            filters={apiCredentialFilters}
            onChange={setSearchInputs}
            onSearch={setSearchGeneral}
          />
        )}

        <MantineProvider theme={rpaTheme}>
          <RpiTableContainer
            data-testid="api-credentials-table"
            footer={<Pagination isLoading={isLoading} pagination={pagination} label="API Credentials" />}
          >
            <RpiTable
              emptyMessage={
                searchInputs?.length || searchGeneral
                  ? "No API credentials found."
                  : "Your API credentials will appear here."
              }
              isLoading={isLoading}
              error={error}
              perPage={apiCredentials?.length}
              data={apiCredentials}
              columns={apiCredentialColumns}
              withSortBy={{ onChange: setSortInput }}
            />
          </RpiTableContainer>
        </MantineProvider>
      </AppPage.Section>

      {(user.is.USER || user.is.CUSTOMER_ADMIN) && (
        <RpiButton
          mt={32}
          width="fit"
          variant="brand-outline"
          size="small"
          children="Create credential"
          data-testid="createApiCredentialButton"
          trailingIcon
          onClick={() => navigateRoute(["api-credential:create"])}
        />
      )}
      {featureFlags.newSearchExperience && user.is.SUPER_ADMIN && (
        <ApiCredentialsFilter
          isOpened={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={searchFilters}
          onApply={setSearchInputs}
        />
      )}
    </AppPage>
  );
};
